import { generateId } from './ids';
import type {
  Tray, TrayMember, Seedling, Tending, Aftertaste,
  LocalTrayState, MemberId, TrayId, PressedSpecimen,
} from './types';

// Re-export types so consumers (App.tsx etc) can import { Seedling, LocalTrayState } from './lib/store' (keeps prior surface)
export type {
  Tray, TrayMember, Seedling, Tending, Connection, Aftertaste,
  LocalTrayState, DemoSeed, MemberId, TrayId, PressedSpecimen,
} from './types';

const LS_MY_TRAYS = 'ct:my-trays'; // [{trayId, myMemberId, code}]
const LS_TRAY_PREFIX = 'ct:tray:';   // full local state per tray

export interface MyTrayRef {
  trayId: TrayId;
  myMemberId: MemberId;
  code: string;
  lastOpened?: number;
}

let memory = {
  myTrays: [] as MyTrayRef[],
  current: null as LocalTrayState | null,
};

function loadMyTrays() {
  try {
    const raw = localStorage.getItem(LS_MY_TRAYS);
    if (raw) memory.myTrays = JSON.parse(raw);
  } catch {}
}

function persistMyTrays() {
  try { localStorage.setItem(LS_MY_TRAYS, JSON.stringify(memory.myTrays)); } catch {}
}

function loadTrayState(trayId: TrayId): LocalTrayState | null {
  try {
    const raw = localStorage.getItem(LS_TRAY_PREFIX + trayId);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persistTrayState(state: LocalTrayState) {
  try {
    localStorage.setItem(LS_TRAY_PREFIX + state.tray.id, JSON.stringify(state));
  } catch {}
}

// --- Public API ---

export function getMyTrays(): MyTrayRef[] {
  loadMyTrays();
  return [...memory.myTrays].sort((a, b) => (b.lastOpened || 0) - (a.lastOpened || 0));
}

export function rememberTray(ref: MyTrayRef) {
  loadMyTrays();
  const idx = memory.myTrays.findIndex(t => t.trayId === ref.trayId);
  if (idx >= 0) memory.myTrays[idx] = { ...memory.myTrays[idx], ...ref };
  else memory.myTrays.push(ref);
  persistMyTrays();
}

export function forgetTray(trayId: TrayId) {
  loadMyTrays();
  memory.myTrays = memory.myTrays.filter(t => t.trayId !== trayId);
  persistMyTrays();
  localStorage.removeItem(LS_TRAY_PREFIX + trayId);
}

export function getCurrentTray(): LocalTrayState | null {
  return memory.current;
}

export function setCurrentTray(state: LocalTrayState) {
  memory.current = state;
  persistTrayState(state);
  // update last opened
  const ref = memory.myTrays.find(t => t.trayId === state.tray.id);
  if (ref) {
    ref.lastOpened = Date.now();
    persistMyTrays();
  }
}

// Create a brand new tray locally (demo or pre-sync)
export function createTrayLocal(name?: string): LocalTrayState {
  const trayId = generateId();
  const code = makeJoinCode();
  const now = Date.now();

  const tray: Tray = {
    id: trayId,
    code,
    created_at: now,
    name: name || 'our tray',
    cell_count: 16,
  };

  const myId = generateId();
  const me: TrayMember = {
    id: myId,
    tray_id: trayId,
    tendril_name: pickStartingTendril(),
    joined_at: now,
  };

  const state: LocalTrayState = {
    tray,
    members: [me],
    seedlings: [],
    tendings: [],
    connections: [],
    aftertastes: [],
    myMemberId: myId,
  };

  // Seed a couple of beautiful example seedlings so the tray feels alive on first open
  seedBeautifulDemo(state);

  rememberTray({ trayId, myMemberId: myId, code, lastOpened: now });
  setCurrentTray(state);
  return state;
}

export function joinTrayLocal(code: string, chosenTendril?: string): LocalTrayState {
  // In pure local we just create a new "mirror" of the same code for demo purposes.
  // Real join against server will merge properly.
  const existing = getMyTrays().find(t => t.code.toUpperCase() === code.toUpperCase());
  if (existing) {
    const loaded = loadTrayState(existing.trayId);
    if (loaded) {
      loaded.myMemberId = existing.myMemberId;
      setCurrentTray(loaded);
      return loaded;
    }
  }

  const trayId = generateId();
  const now = Date.now();
  const tray: Tray = {
    id: trayId,
    code: code.toUpperCase(),
    created_at: now - 1000 * 3600 * 24 * 3, // pretend older
    name: 'our tray',
    cell_count: 16,
  };

  const myId = generateId();
  const me: TrayMember = {
    id: myId,
    tray_id: trayId,
    tendril_name: chosenTendril || pickStartingTendril(),
    joined_at: now,
  };

  const state: LocalTrayState = {
    tray,
    members: [me],
    seedlings: [],
    tendings: [],
    connections: [],
    aftertastes: [],
    myMemberId: myId,
  };

  seedBeautifulDemo(state, true); // different seed for "joined" feel

  rememberTray({ trayId, myMemberId: myId, code: tray.code, lastOpened: now });
  setCurrentTray(state);
  return state;
}

export function plantSeedling(
  content: string,
  attention_source: string | undefined,
  species: string,
  cell_index: number,
  extra?: Partial<Pick<Seedling, 'repair_readiness' | 'intention' | 'impact' | 'accountability' | 'followups' | 'emotion_tags'>>
): Seedling {
  const state = getCurrentTray();
  if (!state) throw new Error('No current tray');

  const now = Date.now();
  const seedling: Seedling = {
    id: generateId(),
    tray_id: state.tray.id,
    cell_index,
    planter_member_id: state.myMemberId,
    planted_at: now,
    content: content.trim(),
    attention_source: attention_source?.trim() || undefined,
    species: species.trim() || 'systems noticing',
    status: 'active',
    repair_readiness: extra?.repair_readiness,
    intention: extra?.intention,
    impact: extra?.impact,
    accountability: extra?.accountability,
    followups: extra?.followups,
    emotion_tags: extra?.emotion_tags,
  };

  state.seedlings = [...state.seedlings.filter(s => s.cell_index !== cell_index), seedling];
  setCurrentTray(state);
  return seedling;
}

export function addTending(seedlingId: string, kind: Tending['kind'], content: string, meta?: any): Tending {
  const state = getCurrentTray();
  if (!state) throw new Error('No current tray');

  const tending: Tending = {
    id: generateId(),
    seedling_id: seedlingId,
    member_id: state.myMemberId,
    tended_at: Date.now(),
    kind,
    content: content.trim(),
    meta_json: meta ? JSON.stringify(meta) : undefined,
  };

  state.tendings = [...state.tendings, tending];

  // Grow the seedling visually (client only)
  const s = state.seedlings.find(x => x.id === seedlingId);
  if (s) {
    // status bump is purely visual via count of tendings in UI
  }

  setCurrentTray(state);
  return tending;
}

export function addAftertaste(seedlingId: string, bodily: string, recovery: string, share: boolean): Aftertaste {
  const state = getCurrentTray();
  if (!state) throw new Error('No current tray');

  const at: Aftertaste = {
    id: generateId(),
    seedling_id: seedlingId,
    member_id: state.myMemberId,
    recorded_at: Date.now(),
    bodily_feeling: bodily.trim(),
    recovery_note: recovery.trim(),
    shared_to_spread: share,
  };
  state.aftertastes = [...state.aftertastes.filter(a => !(a.seedling_id === seedlingId && a.member_id === state.myMemberId)), at];
  setCurrentTray(state);
  return at;
}

export function updateSpreadLayout(patch: Record<string, { x: number; y: number; rot?: number }>) {
  const state = getCurrentTray();
  if (!state) return;
  const now = Date.now();
  state.layout = {
    tray_id: state.tray.id,
    layout: { ...(state.layout?.layout || {}), ...patch },
    updated_at: now,
  };
  setCurrentTray(state);
}

export function getPressedSpecimens(): PressedSpecimen[] {
  const state = getCurrentTray();
  if (!state) return [];
  const presses = state.tendings.filter(t => t.kind === 'press');
  return presses.map(p => {
    const seedling = state.seedlings.find(s => s.id === p.seedling_id)!;
    const member = state.members.find(m => m.id === p.member_id)!;
    const after = state.aftertastes.find(a => a.seedling_id === p.seedling_id && a.member_id === p.member_id);
    return { tending: p, seedling, member, aftertaste: after };
  });
}

export function getOpenGrafts() {
  const state = getCurrentTray();
  if (!state) return [];
  return state.tendings
    .filter(t => t.kind === 'graft')
    .map(t => {
      const s = state.seedlings.find(x => x.id === t.seedling_id);
      const m = state.members.find(x => x.id === t.member_id);
      let meta: any = {};
      try { meta = t.meta_json ? JSON.parse(t.meta_json) : {}; } catch {}
      return { tending: t, seedling: s, member: m, meta };
    });
}

export function recordGraftCompletion(tendingId: string) {
  const state = getCurrentTray();
  if (!state) return;
  const t = state.tendings.find(x => x.id === tendingId);
  if (!t || t.kind !== 'graft') return;
  let meta: any = {};
  try { meta = t.meta_json ? JSON.parse(t.meta_json) : {}; } catch {}
  meta.completed_at = Date.now();
  t.meta_json = JSON.stringify(meta);
  setCurrentTray(state);
}

// --- Demo seeding (so the app is immediately meaningful and beautiful) ---

const TENDRILS = [
  'the curtain rememberer',
  'the kettle watcher',
  'porch sitter',
  'the one who stops three texts',
  'late night photo looker',
  'the kitchen light one',
  'back-of-the-mind texter',
  'the one who remembers the garden',
];

function pickStartingTendril() {
  return TENDRILS[Math.floor(Math.random() * TENDRILS.length)];
}

function makeJoinCode() {
  const words = ['ROOT', 'MOSS', 'KITCH', 'PORCH', 'LIGHT', 'KETTLE', 'CURT', 'SLOW'];
  const w = words[Math.floor(Math.random() * words.length)];
  const n = Math.floor(1000 + Math.random() * 9000).toString().slice(0, 4);
  return `${w}-${n}`;
}

function seedBeautifulDemo(state: LocalTrayState, joined = false) {
  const now = Date.now();
  // Reframed demo seeds: scattered systems wisdom after personal/collective transitions.
  // Each carries repair readiness, intention/impact separation, accountability specificity, follow-ups, and granular emotions.
  const demo: any[] = joined ? [
    {
      tendril: 'the systems mapper', content: 'After the re-org announcement, the feedback loop that used to catch client risk at week 3 now routes through two new managers who have never met the client.', attention: 'reading the Slack thread at 11pm the night before the town hall', species: 'broken loop', cell: 2,
      repair_readiness: 38, intention: 'Protect the team from whiplash by centralizing decisions', impact: 'The people who actually see the risk early have no line of sight to leadership anymore.',
      accountability: 'The new VP of Delivery can reinstate the old 3-person risk huddle by Friday memo.', followups: [{actor:'VP Delivery', action:'send memo restoring client-risk huddle', timeframe:'this week'}],
      emotion_tags: ['Fractured competence', 'Loop made visible']
    },
    {
      tendril: 'quiet archivist', content: 'In the final all-hands of the collective, three people said “we always knew the funding model would break us” but no one had written it down where decisions were made.', attention: 'in the zoom window, watching names disappear from the participant list', species: 'unwritten knowing', cell: 11,
      repair_readiness: 71, intention: 'Honor the work by not pretending it was a surprise', impact: 'The same three people are now being asked to “document lessons” with no power to change the next structure.',
      accountability: 'The two remaining stewards will co-author a one-page “funding assumptions we ignored” and send to the fiscal host before the final disbursement.', followups: [{actor:'two stewards', action:'co-author and send one-page assumptions document', timeframe:'before disbursement'}],
      emotion_tags: ['Muffled rupture', 'Resonant shame']
    }
  ] : [
    {
      tendril: 'the kettle watcher', content: 'After the project ended and the last Slack was archived, I realized the person who always caught the “too quiet” client was never in the retro because we scheduled it for 9am their time.', attention: 'back porch after dinner, scrolling the old notion pages', species: 'missed boundary', cell: 1,
      repair_readiness: 62, intention: 'Run a clean retrospective so everyone could move on', impact: 'The person whose pattern recognition we relied on most was structurally excluded from the only moment the system could learn.',
      accountability: 'Next time a cross-timezone project closes, the retro owner books two slots (or records + async) and explicitly invites the “quiet pattern people”.', followups: [{actor:'retro owner (rotating)', action:'book dual-slot retro or async equivalent + tag the pattern people', timeframe:'within 48h of end'}],
      emotion_tags: ['Slow unspooling', 'Leverage misread']
    },
    {
      tendril: 'the one who remembers the garden', content: 'During the move-out of the shared studio, the person who had been paying the internet bill for three years in her own name finally said it out loud. No one had ever offered to split it.', attention: 'walking the dog at dusk, watching the lights go off in the old windows', species: 'invisible cost surfaced', cell: 9,
      repair_readiness: 29, intention: 'Keep the space running with as little admin friction as possible', impact: 'One person quietly carried a recurring cost that became a quiet resentment and a power imbalance no one named until it was too late.',
      accountability: 'The three longest members will each Venmo the former bill-payer their share of the last 18 months within 10 days and log it in the shared ledger.', followups: [{actor:'three longest members', action:'Venmo exact shares for 18 months internet + log in ledger', timeframe:'within 10 days'}],
      emotion_tags: ['Tangled belonging', 'Cost that surfaced late']
    }
  ];

  demo.forEach((d, i) => {
    const planter = state.members[0];
    const s: Seedling = {
      id: generateId(),
      tray_id: state.tray.id,
      cell_index: d.cell,
      planter_member_id: planter.id,
      planted_at: now - (1000 * 3600 * (12 + i * 7)),
      content: d.content,
      attention_source: d.attention,
      species: d.species,
      status: 'active',
      repair_readiness: d.repair_readiness,
      intention: d.intention,
      impact: d.impact,
      accountability: d.accountability,
      followups: d.followups,
      emotion_tags: d.emotion_tags,
    };
    state.seedlings.push(s);

    if (!joined) {
      const otherName = TENDRILS[(i + 3) % TENDRILS.length];
      let other = state.members.find(m => m.tendril_name === otherName);
      if (!other) {
        other = { id: generateId(), tray_id: state.tray.id, tendril_name: otherName, joined_at: now - 100000 };
        state.members.push(other);
      }
      state.tendings.push({
        id: generateId(),
        seedling_id: s.id,
        member_id: other.id,
        tended_at: now - 1000 * 3600 * (3 + i),
        kind: 'witness',
        content: i === 0 ? 'That same timezone exclusion also meant the risk log never saw the quiet signals.' : 'I had noticed the bill name but never asked out loud. That was on me.',
      });
    }
  });
}

// --- Server sync stubs (real implementations live in the worker) ---

const API = '/api';

export async function apiCreateTray(name?: string): Promise<{ tray: Tray; myMember: TrayMember; code: string }> {
  const res = await fetch(`${API}/tray`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error('create failed');
  return res.json();
}

export async function apiJoinTray(code: string, tendril: string): Promise<{ tray: Tray; myMember: TrayMember }> {
  const res = await fetch(`${API}/tray/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, tendril_name: tendril }),
  });
  if (!res.ok) throw new Error('join failed');
  return res.json();
}

export async function apiPlant(trayId: string, memberId: string, payload: any) {
  const res = await fetch(`${API}/tray/${trayId}/plant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Member-Id': memberId },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function apiTend(trayId: string, memberId: string, seedlingId: string, payload: any) {
  const res = await fetch(`${API}/tray/${trayId}/tend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Member-Id': memberId },
    body: JSON.stringify({ seedling_id: seedlingId, ...payload }),
  });
  return res.json();
}

export async function apiAftertaste(trayId: string, memberId: string, seedlingId: string, payload: any) {
  const res = await fetch(`${API}/tray/${trayId}/aftertaste`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Member-Id': memberId },
    body: JSON.stringify({ seedling_id: seedlingId, ...payload }),
  });
  return res.json();
}

export async function apiUploadAttachment(trayId: string, memberId: string, seedlingId: string, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('seedling_id', seedlingId);
  const res = await fetch(`${API}/tray/${trayId}/attach`, {
    method: 'POST',
    headers: { 'X-Member-Id': memberId },
    body: fd,
  });
  return res.json();
}

export async function apiGetTray(trayId: string, memberId?: string): Promise<LocalTrayState | null> {
  const headers: any = {};
  if (memberId) headers['X-Member-Id'] = memberId;
  const res = await fetch(`${API}/tray/${trayId}`, { headers });
  if (!res.ok) return null;
  return res.json();
}